import { FormEventHandler, useMemo, useState } from 'react'
import slugify from 'slugify'
import { ObjectProperty } from '../../shared/types'
import { Button } from '../components/button'
import { Input } from '../components/input'
import { Select } from '../components/select'
import { Switch } from '../components/switch'
import { Textarea } from '../components/textarea'

type Props = {
	object: string
	data?: ObjectProperty
	onSubmit: FormEventHandler
	isPending?: boolean
}

const OPTIONS = [
	{ value: 'string', label: 'text' },
	{ value: 'text', label: 'dlouhý text' },
	{ value: 'number', label: 'číslo' },
	{ value: 'currency', label: 'částka (měna)' },
	{ value: 'boolean', label: 'ano/ne' },
	{ value: 'single', label: 'výběr' },
	{ value: 'multi', label: 'vícenásobný výběr' },
	{ value: 'datetime', label: 'datum a čas' },
	{ value: 'reference', label: 'relace' },
	{ value: 'derived', label: 'výpočet' },
	{ value: 'cloudinary', label: 'obrázek' },
]

const REFERENCES = [
	{ value: 'client', label: 'Zákazník' },
	{ value: 'user', label: 'Uživatel' },
	{ value: 'vehicle', label: 'Vozidlo' },
	{ value: 'location', label: 'Pobočka' },
	{ value: 'brand', label: 'Pronajímatel' },
	{ value: 'reservation', label: 'Rezervace' },
	{ value: 'proposal_vehicle', label: 'Vozidlo poptávky' },
	{ value: 'proposal', label: 'Nabídka' },
]

const SLUGIFY_OPTIONS = {
	lower: true,
	replacement: '_',
	strict: true,
}

export function ObjectPropertiesForm({ object, data, onSubmit, isPending }: Props) {
	const [type, setType] = useState<ObjectProperty['type']>(data?.type ?? 'string')
	const [options, setOptions] = useState(data?.options ?? [])
	const references = useMemo(() => {
		return REFERENCES.filter(({ value }) => value !== object)
	}, [object])
	const [autoIdentifier, setAutoIdentifier] = useState(() => data?.identifier)
	const [identifier, setIdentifier] = useState<string>()
	const [isPositive, setIsPositive] = useState(data?.isPositive ?? false)

	const handleNameChange = (value: string) => {
		if (data?.identifier) return
		setAutoIdentifier(slugify(value, SLUGIFY_OPTIONS))
	}

	const renderDefaultValueInput = () => {
		switch (type) {
			case 'string':
				return (
					<Input
						label="Výchozí hodnota"
						name="defaultValue"
						defaultValue={data?.defaultValue as string | undefined}
					/>
				)
			case 'number':
			case 'currency':
				return (
					<Input
						label="Výchozí hodnota"
						name="defaultValue"
						type="number"
						min={isPositive ? 0 : undefined}
						defaultValue={data?.defaultValue?.toString()}
					/>
				)
			case 'single':
				return (
					<Select
						label="Výchozí hodnota"
						name="defaultValue"
						span="FULL"
						options={options.map(opt => ({ value: opt.id as string, label: opt.name }))}
						defaultSelectedKey={data?.defaultValue as string | undefined}
					/>
				)
			case 'boolean':
				return (
					<Switch
						label="Výchozí hodnota"
						name="defaultValue"
						defaultSelected={data?.defaultValue as boolean | undefined}
					/>
				)
			default:
				return null
		}
	}

	return (
		<form className="space-y-4 m-auto max-w-[60em]" onSubmit={onSubmit}>
			<input type="hidden" name="object" value={object} />
			<h3 className="text-md font-bold">{data ? 'Editace vlastnosti' : 'Nová vlastnost'}</h3>
			<div className="grid grid-cols-2 gap-4">
				<Input
					label="Název"
					name="name"
					maxLength={255}
					isRequired
					defaultValue={data?.name}
					onChange={handleNameChange}
					className="col-span-2"
				/>
				<Input
					label="Identifikátor"
					name="identifier"
					maxLength={255}
					isRequired
					value={identifier ?? autoIdentifier}
					onChange={setIdentifier}
					isReadOnly={!!data}
					className="col-span-2"
					pattern="^[a-z][a-z0-9_]*"
				/>
				<Select
					span="FULL"
					className="w-full col-span-2"
					label="Typ vlastnosti"
					name="type"
					isRequired
					options={OPTIONS}
					selectedKey={type}
					onSelectionChange={type => setType(type as ObjectProperty['type'])}
				/>
				{type === 'reference' ? (
					<Select
						span="FULL"
						className="col-span-2"
						label="Relace"
						name="referenceTo"
						isRequired
						options={references}
						defaultSelectedKey={data?.referenceTo}
					/>
				) : null}
				<Textarea
					label="Popis"
					name="description"
					className="col-span-2"
					defaultValue={data?.description}
				/>
				{type === 'single' || type === 'multi' ? (
					<div className="space-y-2">
						<input
							type="hidden"
							name="options"
							value={JSON.stringify(options.filter(option => option.name))}
						/>
						<h4>Položky</h4>
						{options.map((option, index) => {
							return (
								<div key={option.id ?? index} className="flex gap-2 items-center">
									<span className="text-right w-8">{index + 1}.</span>{' '}
									<Input
										aria-label="Položka"
										value={option.name}
										onChange={value => {
											setOptions(options => {
												return options.map((option, i) => {
													if (index === i) {
														return { ...option, name: value }
													}
													return option
												})
											})
										}}
									/>
									<Button
										variant="TEXT"
										size="SMALL"
										onPress={() => {
											setOptions(options => {
												const newOptions = [...options]
												newOptions.splice(index, 1)
												return newOptions
											})
										}}
									>
										X
									</Button>
								</div>
							)
						})}
						<Button
							variant="SECONDARY"
							onPress={() => {
								setOptions(options => options.concat([{ name: '' }]))
							}}
						>
							+ Přidat položku výběru
						</Button>
					</div>
				) : null}
				<div className="col-span-2">
					<Switch
						label="Skrytá vlastnost"
						name="isHidden"
						defaultSelected={data?.isHidden}
					/>
				</div>
				{type === 'derived' ? (
					<Textarea
						label="Výpočet"
						name="formula"
						className="col-span-2"
						defaultValue={data?.formula}
					/>
				) : null}
				{type === 'number' || type === 'currency' ? (
					<Switch
						label="Pouze kladná čísla"
						name="isPositive"
						defaultSelected={isPositive}
						onChange={setIsPositive}
					/>
				) : null}

				<div className="col-span-2">{renderDefaultValueInput()}</div>
			</div>
			<Button type="submit" isPending={isPending} isDisabled={isPending}>
				Uložit
			</Button>
		</form>
	)
}
